import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { THEMES } from '../../../data/product/product.data';
import { BRAND } from '../../../data/brand/brand.data';
import { Product, ProductLine } from '../../../model/product/product.model';
import { Image } from '../../../model/image/image.model';

/**
 * One rendered row in the screenshots gallery. The page renders the
 * full theme list once with a marketing-led badge driven by each
 * theme's optional `editorialLabel` (e.g. "Editor's Choice", "Staff
 * Pick", "Featured", "Trending", "New Release"). The string is
 * uppercased and shown as the badge value above the static "FEATURED"
 * label. Per-theme - so adding a new theme to the catalogue never
 * invalidates an existing theme's marketplace screenshot. A "CURATED"
 * placeholder is used when a theme hasn't been given an
 * `editorialLabel` yet.
 *
 * The All-In-One collection uses a different badge in the same slot:
 * the running count of individual themes ("N Themes") above a "BUNDLE"
 * label - so the bundle's marketplace screenshot communicates scope
 * instead of an editorial hook.
 *
 * `variant` is retained on the DOM as `data-variant="featured"` so
 * capture tooling can still target the section by attribute.
 */
interface ScreenshotSection {
  /** Unique identifier across the rendered list - just the product key,
   * since each product appears once. Used by `trackBy` and as the
   * rendered `data-key`. */
  trackKey: string;
  /** Exposed on the DOM as `data-variant` so capture tooling can target
   * the section. There is only one pass, but keeping the attribute
   * stable avoids breaking any external tooling that already keys off
   * it. */
  variant: 'featured';
  productLine: ProductLine;
  product: Product;
  screenshot?: Image;
  isCollection: boolean;
  isDarkTheme: boolean;
  /** Brand inline-wordmark variant matching the section's background. */
  brandLogo: string;
  /** The prominent value the template binds to the badge value slot. */
  displayBadge: string;
  /** The small label below the value. */
  displayBadgeLabel: string;
}

@Component( {
  selector: 'app-screenshots-page',
  templateUrl: './screenshots-page.component.html',
  styleUrls: [ './screenshots-page.component.scss' ],
} )
export class ScreenshotsPageComponent implements OnInit, OnDestroy {

  /**
   * Capture mode, the same contract /line and /pinterest already use.
   *
   * `?only=<product-key>` renders that one section, and `?capture=1` strips the
   * layout chrome and blows the section up to fill the viewport exactly, so a
   * headless screenshot of the window IS the marketplace image with no cropping.
   * Without it the section is 88% of the viewport with the nav and footer around
   * it, which is why these posters used to be produced by hand.
   */
  onlyKey: string | null = null;
  capture = false;

  BRAND = BRAND;

  /** Single flattened list - the template iterates this once. */
  sections: ScreenshotSection[] = [];

  constructor( private route: ActivatedRoute ) {
    this.onlyKey = this.route.snapshot.queryParamMap.get( 'only' );
    this.capture = this.route.snapshot.queryParamMap.get( 'capture' ) !== null;
    if ( this.capture && typeof document !== 'undefined' ) {
      document.documentElement.classList.add( 'screenshots-capture' );
    }
  }

  ngOnDestroy(): void {
    if ( typeof document !== 'undefined' ) {
      document.documentElement.classList.remove( 'screenshots-capture' );
    }
  }

  ngOnInit(): void {
    // Number of individual (non-collection) themes - used as the value
    // for the "N Themes" badge on the All-In-One collection section.
    const individualThemeCount = THEMES.reduce( ( acc, productLine ) => {
      if ( this.isCollectionProductLine( productLine ) ) {
        return acc;
      }
      return acc + productLine.products.length;
    }, 0 );

    const sections: ScreenshotSection[] = [];

    for ( const productLine of THEMES ) {
      const isCollection = this.isCollectionProductLine( productLine );
      for ( const product of productLine.products ) {
        const isDarkTheme = product.appearance === 'Dark';
        const screenshot = this.resolveScreenshot( product );
        const brandLogo = isDarkTheme
          ? BRAND.logo.inlineDark
          : BRAND.logo.inlineLight;

        // Collection sections (All-In-One) show "N Themes / BUNDLE" so
        // the badge communicates the bundle's scope. Individual themes
        // show their editorialLabel uppercased above the static
        // "FEATURED" label, falling back to "CURATED" when a theme
        // hasn't been labelled yet.
        const editorialText = product.editorialLabel?.trim()
          ? product.editorialLabel.toUpperCase()
          : 'CURATED';

        sections.push( {
          trackKey: product.key,
          variant: 'featured',
          productLine,
          product,
          screenshot,
          isCollection,
          isDarkTheme,
          brandLogo,
          displayBadge: isCollection
            ? `${ individualThemeCount } Themes`
            : editorialText,
          displayBadgeLabel: isCollection ? 'BUNDLE' : 'FEATURED',
        } );
      }
    }

    this.sections = this.onlyKey
      ? sections.filter( section => section.product.key === this.onlyKey )
      : sections;
  }

  /** A product line is a "collection" if its key is `all-in-one` - the
   * convention used in the existing product data. */
  private isCollectionProductLine( productLine: ProductLine ): boolean {
    return productLine.key === 'all-in-one';
  }

  /** Picks the screenshot to render in the section's screenshot frame: the
   * JetBrains marketplace screenshot. This gallery is the marketplace-screenshot
   * pass, so it shows only the JetBrains screenshot - never LINE/other platforms
   * (e.g. a theme's LINE preview must not surface here). Falls back to the first
   * platform that carries one only if there is no JetBrains screenshot. */
  private resolveScreenshot( product: Product ): Image | undefined {
    const platforms = product.platforms || [];
    const jetbrains = platforms.find( p => p.info?.name === 'JetBrains' );
    return jetbrains?.screenshot
      || platforms.find( platform => platform.screenshot )?.screenshot;
  }

  /** trackBy for the *ngFor over sections. Uses `trackKey` (product key). */
  trackBySectionKey( _index: number, section: ScreenshotSection ): string {
    return section.trackKey;
  }
}
